import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  Calculator,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Compass,
  CornerDownLeft,
  FileText,
  Globe,
  GraduationCap,
  HelpCircle,
  Key,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  Monitor,
  Moon,
  Newspaper,
  Phone,
  Pill,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Stethoscope,
  Sun,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { contentApi, doctorApi } from '../lib/api';
import { organs, diseases, articles, calculators, pharmaDrugs } from '../mock';
import { SEED_DOCTORS } from '../data/doctorsSeed';
import './Navbar.css';

/* ------------------------------------------------------------------ */
/*  NAV CONFIGURATIONS PER ROLE                                        */
/* ------------------------------------------------------------------ */

const GENERAL_NAV = [
  {
    label: 'Learnings',
    Icon: GraduationCap,
    isDropdown: true,
    children: [
      { to: '/anatomy', label: 'Anatomy', Icon: Activity },
      { to: '/pharma', label: 'Pharma', Icon: FileText },
      { to: '/diseases', label: 'Disease', Icon: Stethoscope },
    ],
  },
  { to: '/articles', label: 'News and Article', Icon: Newspaper },
  { to: '/dashboard?tab=channeling', label: 'Consultation', Icon: Stethoscope },
  { to: '/calculators', label: 'Calculators', Icon: Calculator },
  { to: '/library', label: 'Market', Icon: ShoppingCart },
];

const STUDENT_NAV = [
  {
    label: 'Learnings',
    Icon: GraduationCap,
    isDropdown: true,
    children: [
      { to: '/anatomy', label: 'Anatomy', Icon: Activity },
      { to: '/pharma', label: 'Pharma', Icon: FileText },
      { to: '/diseases', label: 'Disease', Icon: Stethoscope },
    ],
  },
  { to: '/articles', label: 'News and Article', Icon: Newspaper },
  { to: '/dashboard?tab=channeling', label: 'Consultation', Icon: Stethoscope },
  { to: '/calculators', label: 'Calculators', Icon: Calculator },
  { to: '/library', label: 'Market', Icon: ShoppingCart },
];

const DOCTOR_NAV = [
  { to: '/doctor/dashboard', label: 'Doctor Hub', Icon: LayoutDashboard },
  {
    label: 'Learnings',
    Icon: GraduationCap,
    isDropdown: true,
    children: [
      { to: '/anatomy', label: 'Anatomy', Icon: Activity },
      { to: '/pharma', label: 'Pharma', Icon: FileText },
      { to: '/diseases', label: 'Disease', Icon: Stethoscope },
    ],
  },
  { to: '/articles', label: 'News and Article', Icon: Newspaper },
  { to: '/doctor/appointments', label: 'Consultations', Icon: Calendar },
  { to: '/calculators', label: 'Calculators', Icon: Calculator },
  { to: '/library', label: 'Market', Icon: ShoppingCart },
];

const ADMIN_NAV = [
  {
    label: 'Learnings',
    Icon: GraduationCap,
    isDropdown: true,
    children: [
      { to: '/anatomy', label: 'Anatomy', Icon: Activity },
      { to: '/pharma', label: 'Pharma', Icon: FileText },
      { to: '/diseases', label: 'Disease', Icon: Stethoscope },
    ],
  },
  { to: '/articles', label: 'News and Article', Icon: Newspaper },
  { to: '/dashboard?tab=channeling', label: 'Consultation', Icon: Stethoscope },
  { to: '/calculators', label: 'Calculators', Icon: Calculator },
  { to: '/library', label: 'Market', Icon: ShoppingCart },
];

// Mirrors the ROUTES map in Calculators.jsx — only calculators that have a page
// are worth offering in search.
const CALCULATOR_ROUTES = { bmi: '/calculators/bmi', ascvd: '/calculators/ascvd', crcl: '/calculators/crcl' };

// Detail pages are the long reads, and the only ones where a progress line
// tells the reader anything useful.
const READING_ROUTES = ['/articles/', '/anatomy/', '/diseases/'];

const isActiveNav = (location, to) => {
  if (to.includes('?')) {
    const [path, query] = to.split('?');
    if (location.pathname !== path) return false;
    const urlParams = new URLSearchParams(query);
    const locParams = new URLSearchParams(location.search);
    for (const [key, val] of urlParams.entries()) {
      if (locParams.get(key) !== val) return false;
    }
    return true;
  }
  return location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
};

// Build comprehensive base index covering all content across the website
const buildBaseSiteIndex = () => {
  const list = [];

  // 1. Anatomy Organs
  (organs || []).forEach((o) => {
    list.push({
      id: `anatomy:${o.slug}`,
      label: o.name || o.title,
      meta: o.system ? `${o.system} · Anatomy` : 'Human Anatomy',
      to: `/anatomy/${o.slug}`,
      kind: 'Anatomy',
      Icon: Activity,
      keywords: `${o.name || ''} ${o.system || ''} ${o.desc || ''} anatomy organ human body biology system`.toLowerCase(),
    });
  });

  // 2. Diseases & Conditions
  (diseases || []).forEach((d) => {
    list.push({
      id: `disease:${d.slug}`,
      label: d.name || d.title,
      meta: d.system || d.category ? `${d.system || d.category} · Disease` : 'Clinical Pathology',
      to: `/diseases/${d.slug}`,
      kind: 'Disease',
      Icon: Stethoscope,
      keywords: `${d.name || ''} ${d.system || ''} ${d.category || ''} ${d.desc || ''} ${(d.symptoms || []).join(' ')} disease illness pathology diagnosis clinical symptom condition`.toLowerCase(),
    });
  });

  // 3. Pharmacology & Medicines
  (pharmaDrugs || []).forEach((d) => {
    list.push({
      id: `drug:${d.slug || d.id}`,
      label: d.name || d.genericName,
      meta: d.category ? `${d.category} · ${d.class || 'Medicine'}` : (d.class || 'Pharmacology'),
      to: `/pharma/${d.slug || d.id}`,
      kind: 'Medicine',
      Icon: Pill,
      keywords: `${d.name || ''} ${d.genericName || ''} ${d.class || ''} ${d.category || ''} ${d.desc || ''} ${d.dose || ''} medicine drug pharma pharmacology tablet pill prescription dosage`.toLowerCase(),
    });
  });

  // 4. Clinical Articles & Editorial
  (articles || []).forEach((a) => {
    list.push({
      id: `article:${a.slug}`,
      label: a.title || a.name,
      meta: a.category ? `${a.category} · ${a.author?.name || 'Dr. Pulse'}` : 'Clinical Editorial',
      to: `/articles/${a.slug}`,
      kind: 'Article',
      Icon: Newspaper,
      keywords: `${a.title || ''} ${a.category || ''} ${a.excerpt || ''} ${a.author?.name || ''} news article medical clinical journal evidence guide update`.toLowerCase(),
    });
  });

  // 5. Medical Calculators
  (calculators || []).forEach((c) => {
    list.push({
      id: `calc:${c.id}`,
      label: c.name,
      meta: `${c.category || 'Bedside'} · Medical Calculator`,
      to: CALCULATOR_ROUTES[c.id] || '/calculators',
      kind: 'Calculator',
      Icon: Calculator,
      keywords: `${c.name || ''} ${c.desc || ''} ${c.category || ''} calculator formula risk score bedside clinical tool assessment`.toLowerCase(),
    });
  });

  // 6. Certified Doctors & Specialists
  (SEED_DOCTORS || []).forEach((doc) => {
    list.push({
      id: `doctor:${doc.id}`,
      label: doc.full_name || `Dr. ${doc.name || 'Specialist'}`,
      meta: `${doc.specialization || 'Consultant'} · Doctor Channeling`,
      to: '/dashboard?tab=channeling',
      kind: 'Doctor',
      Icon: UserCheck,
      keywords: `${doc.full_name || ''} ${doc.specialization || ''} ${doc.clinic_address || ''} ${doc.hospital_affiliation || ''} ${doc.qualifications || ''} doctor physician specialist channeling appointment book consultation clinic hospital fee`.toLowerCase(),
    });
  });

  // 7. Campus LMS Modules & Courses
  const lmsEntries = [
    { label: 'Anatomy & Embryology LMS Course', meta: 'Year 1 & 2 Medical Curriculum', to: '/lms', keywords: 'lms course anatomy embryology histology lecture slides campus study module student' },
    { label: 'Pathology & Disease Mechanisms Course', meta: 'Clinical Pathology Curriculum', to: '/lms', keywords: 'lms course pathology disease mechanism histology biopsy campus lecture student' },
    { label: 'Pharmacology & Therapeutics Course', meta: 'Prescribing & Drug Classes Curriculum', to: '/lms', keywords: 'lms course pharmacology therapeutics prescribing drug classes campus lecture student' },
    { label: 'Internal Medicine & Ward Postings', meta: 'Clinical Ward Case Studies', to: '/lms', keywords: 'lms course internal medicine clinical postings case study rounds student' },
    { label: 'General Surgery & Trauma Modules', meta: 'Surgical Skills & Operative Curriculum', to: '/lms', keywords: 'lms course surgery surgical skills trauma theatre posting student' },
    { label: 'Pediatrics & Neonatal Care Module', meta: 'Child Health Curriculum', to: '/lms', keywords: 'lms course pediatrics child health neonatal pediatric vaccine milestones student' },
    { label: 'Campus Medical LMS Portal', meta: 'Timetables, Lecture Notes & Exams', to: '/lms', keywords: 'lms campus student portal timetable lecture schedule assignment exam grades faculty' },
  ];
  lmsEntries.forEach((entry, idx) => {
    list.push({
      id: `lms:${idx}`,
      label: entry.label,
      meta: entry.meta,
      to: entry.to,
      kind: 'LMS Course',
      Icon: GraduationCap,
      keywords: `${entry.label} ${entry.meta} ${entry.keywords}`.toLowerCase(),
    });
  });

  // 8. Core Website Navigation Hubs
  const pageEntries = [
    { label: 'Anatomy Explorer', meta: 'Interactive 3D Body Systems & Organs', to: '/anatomy', keywords: 'anatomy 3d body systems organs nervous cardiovascular musculoskeletal explorer human' },
    { label: 'Disease & Pathology Directory', meta: 'Clinical Pathology & Health Guides', to: '/diseases', keywords: 'diseases conditions symptoms treatments pathology directory library illness diagnosis' },
    { label: 'Pharmacology Drug Database', meta: 'Generic Medications Reference', to: '/pharma', keywords: 'pharmacology medicines drugs generic brand dosage interactions reference database' },
    { label: 'News and Clinical Articles', meta: 'Clinical Editorial & Insights', to: '/articles', keywords: 'news articles clinical practice journal radiology updates editorial guide' },
    { label: 'Doctor Channeling & Consultation', meta: 'Channel Verified Doctors & Book Slots', to: '/dashboard?tab=channeling', keywords: 'consultation channeling book doctor specialist appointments clinics fees physician' },
    { label: 'Bedside Medical Calculators', meta: 'Validated Clinical Scores & Formulas', to: '/calculators', keywords: 'calculators bedside bmi ascvd crcl curb65 glasgow scores formulas risk' },
    { label: 'Medical Library & Bookstore', meta: 'High-Yield PDFs & Clinical Textbooks', to: '/library', keywords: 'library books bookstore purchase pdf ebooks text reading shop store buy' },
    { label: 'University Campus LMS', meta: 'Medical Student Course Portal', to: '/lms', keywords: 'lms campus student university courses lectures timetables assignments grades' },
    { label: 'Interactive Medical Quiz', meta: 'Clinical Practice Questions & Tests', to: '/quiz', keywords: 'quiz test questions mcqs knowledge self assessment practice timed exam' },
    { label: 'My Appointments', meta: 'View Doctor Consultations Schedule', to: '/dashboard?tab=appointments', keywords: 'appointments my bookings scheduled channelings doctor visit status' },
    { label: 'Prescriptions & Clinical Notes', meta: 'Access Official Doctor Prescriptions', to: '/dashboard?tab=prescriptions', keywords: 'prescriptions doctor notes rx medicines dosage clinical instructions records' },
  ];
  pageEntries.forEach((page) => {
    list.push({
      id: `page:${page.to}`,
      label: page.label,
      meta: page.meta,
      to: page.to,
      kind: 'Page',
      Icon: Compass,
      keywords: `${page.label} ${page.meta} ${page.keywords} page section website navigation link`.toLowerCase(),
    });
  });

  return list;
};

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [index, setIndex] = useState(() => buildBaseSiteIndex());
  const [indexLoaded, setIndexLoaded] = useState(false);
  const [indexLoading, setIndexLoading] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pill, setPill] = useState({ left: 0, width: 0, visible: false });
  const [learningsOpen, setLearningsOpen] = useState(false);
  const [drawerLearningsOpen, setDrawerLearningsOpen] = useState(false);
  // Profile menu accordion – which section is expanded (null = none)
  const [profileSection, setProfileSection] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, updateUserProfile } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const isAdmin = Boolean(
    user &&
    user.is_admin &&
    user.email?.toLowerCase().trim() === 'admin@gmail.com'
  );
  const userRole = (user?.role || '').toLowerCase().trim();
  const isUpgraded =
    userRole === 'doctor' ||
    userRole === 'student' ||
    ((user?.is_approved === true || user?.verification_status === 'approved') &&
      ((user?.verification_requested_role || '').toLowerCase() === 'student' ||
       (user?.verification_requested_role || '').toLowerCase() === 'doctor' ||
       Boolean(user?.student_id_front) ||
       Boolean(user?.college_name) ||
       Boolean(user?.license_number)));

  const isDoctor = !isAdmin && (userRole === 'doctor' || (isUpgraded && (user?.verification_requested_role === 'doctor' || Boolean(user?.license_number))));
  const isStudent = !isAdmin && !isDoctor && (userRole === 'student' || isUpgraded);
  const isGeneral = !isAdmin && !isDoctor && !isStudent;
  const navList = isAdmin ? ADMIN_NAV : isDoctor ? DOCTOR_NAV : isStudent ? STUDENT_NAV : GENERAL_NAV;

  const navRef = useRef(null);
  const itemRefs = useRef({});
  const searchRef = useRef(null);
  const accountRef = useRef(null);
  const learningsRef = useRef(null);
  // Scroll handling reads this without re-subscribing on every state change.
  const blockHide = useRef(false);
  useEffect(() => {
    blockHide.current = drawerOpen || searchOpen || accountOpen;
  }, [drawerOpen, searchOpen, accountOpen]);

  const showProgress = READING_ROUTES.some((prefix) => location.pathname.startsWith(prefix) && location.pathname.length > prefix.length);

  // --- scroll progress and mobile hide/reveal ------------------------------
  useEffect(() => {
    let last = window.scrollY;
    let travelled = 0;
    let frame = 0;

    // Distance the reader has to keep going in one direction before the bar
    // reacts. Flipping on raw direction makes it flicker on the small
    // reversals a trackpad or a phone's momentum produces.
    const HIDE_AFTER = 64;
    const SHOW_AFTER = 28;
    const ALWAYS_SHOW_ABOVE = 180;

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const doc = document.documentElement;
        const max = doc.scrollHeight - doc.clientHeight;
        const delta = y - last;
        last = y;

        setProgress(max > 0 ? Math.min(1, y / max) : 0);

        // A change of direction starts the count again.
        if ((delta > 0) !== (travelled > 0)) travelled = 0;
        travelled += delta;

        if (y < ALWAYS_SHOW_ABOVE || blockHide.current) {
          setHidden(false);
          travelled = 0;
        } else if (travelled > HIDE_AFTER) {
          setHidden(true);
          travelled = 0;
        } else if (travelled < -SHOW_AFTER) {
          setHidden(false);
          travelled = 0;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // --- the active pill follows the current route ---------------------------
  const measurePill = useCallback(() => {
    const container = navRef.current;
    // Flatten all nav items (including dropdown children) for pill matching
    const allItems = [];
    navList.forEach((n) => {
      if (n.isDropdown && n.children) {
        n.children.forEach((c) => allItems.push(c));
      } else if (!n.disabled && !n.highlight) {
        allItems.push(n);
      }
    });
    const active = allItems.find((n) => n.to && isActiveNav(location, n.to));
    const element = active && itemRefs.current[active.to];
    if (!container || !element) {
      setPill((current) => ({ ...current, visible: false }));
      return;
    }
    setPill({
      left: element.offsetLeft,
      width: element.offsetWidth,
      visible: true,
    });
  }, [location, navList]);

  useLayoutEffect(() => { measurePill(); }, [measurePill]);

  useEffect(() => {
    const container = navRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measurePill);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measurePill]);

  // --- comprehensive search indexer -----------------------------------------
  const loadIndex = useCallback(() => {
    if (indexLoaded || indexLoading) return;
    setIndexLoading(true);
    Promise.all([
      contentApi.listOrgans().catch(() => []),
      contentApi.listDiseases().catch(() => []),
      contentApi.listArticles().catch(() => []),
      contentApi.listDrugs().catch(() => []),
      contentApi.listBooks().catch(() => []),
      doctorApi.listApprovedDoctors().catch(() => []),
    ]).then(([organsData, diseasesData, articlesData, drugsData, booksData, doctorsData]) => {
      const dynamicList = [];

      (organsData || []).forEach((o) => {
        dynamicList.push({
          id: `anatomy:${o.slug}`,
          label: o.name || o.title,
          meta: o.system ? `${o.system} · Anatomy` : 'Human Anatomy',
          to: `/anatomy/${o.slug}`,
          kind: 'Anatomy',
          Icon: Activity,
          keywords: `${o.name || ''} ${o.system || ''} ${o.desc || ''} anatomy organ human body`.toLowerCase(),
        });
      });

      (diseasesData || []).forEach((d) => {
        dynamicList.push({
          id: `disease:${d.slug}`,
          label: d.name || d.title,
          meta: d.system || d.category ? `${d.system || d.category} · Disease` : 'Clinical Pathology',
          to: `/diseases/${d.slug}`,
          kind: 'Disease',
          Icon: Stethoscope,
          keywords: `${d.name || ''} ${d.system || ''} ${d.category || ''} ${d.desc || ''} ${(d.symptoms || []).join(' ')} disease condition pathology`.toLowerCase(),
        });
      });

      (drugsData || []).forEach((d) => {
        dynamicList.push({
          id: `drug:${d.slug || d.id}`,
          label: d.name || d.genericName,
          meta: d.category ? `${d.category} · ${d.class || 'Medicine'}` : (d.class || 'Pharmacology'),
          to: `/pharma/${d.slug || d.id}`,
          kind: 'Medicine',
          Icon: Pill,
          keywords: `${d.name || ''} ${d.genericName || ''} ${d.class || ''} ${d.category || ''} ${d.desc || ''} medicine drug pharmacology`.toLowerCase(),
        });
      });

      (articlesData || []).forEach((a) => {
        dynamicList.push({
          id: `article:${a.slug}`,
          label: a.title || a.name,
          meta: a.category ? `${a.category} · ${a.author?.name || 'Dr. Pulse'}` : 'Clinical Editorial',
          to: `/articles/${a.slug}`,
          kind: 'Article',
          Icon: Newspaper,
          keywords: `${a.title || ''} ${a.category || ''} ${a.excerpt || ''} ${a.author?.name || ''} news article medical`.toLowerCase(),
        });
      });

      (booksData || []).forEach((b) => {
        dynamicList.push({
          id: `book:${b.slug || b.id}`,
          label: b.title,
          meta: b.author ? `${b.author} · ${b.category || 'Medical Book'}` : (b.category || 'Medical Library'),
          to: `/library#book-${b.slug || b.id}`,
          kind: 'Book',
          Icon: BookOpen,
          keywords: `${b.title || ''} ${b.subtitle || ''} ${b.description || ''} ${b.author || ''} book library textbook pdf reading`.toLowerCase(),
        });
      });

      (doctorsData || []).forEach((doc) => {
        dynamicList.push({
          id: `doctor:${doc.id}`,
          label: doc.full_name || `Dr. ${doc.name || 'Specialist'}`,
          meta: `${doc.specialization || 'Consultant'} · Doctor Channeling`,
          to: '/dashboard?tab=channeling',
          kind: 'Doctor',
          Icon: UserCheck,
          keywords: `${doc.full_name || ''} ${doc.specialization || ''} ${doc.clinic_address || ''} ${doc.hospital_affiliation || ''} doctor specialist channeling`.toLowerCase(),
        });
      });

      setIndex((prev) => {
        const map = new Map();
        (prev || []).forEach((item) => map.set(item.id, item));
        dynamicList.forEach((item) => map.set(item.id, item));
        return Array.from(map.values());
      });
      setIndexLoaded(true);
      setIndexLoading(false);
    }).catch(() => {
      setIndexLoading(false);
    });
  }, [indexLoaded, indexLoading]);

  // Pre-load dynamic records as soon as Navbar mounts
  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  // Intelligent multi-category search ranking
  const results = useMemo(() => {
    const raw = query.trim().toLowerCase();
    if (!raw || !index) return [];

    const scored = [];
    for (const entry of index) {
      const label = (entry.label || '').toLowerCase();
      const meta = (entry.meta || '').toLowerCase();
      const kind = (entry.kind || '').toLowerCase();
      const keywords = (entry.keywords || '').toLowerCase();

      let score = 0;

      if (label === raw) {
        score = 1000;
      } else if (label.startsWith(raw)) {
        score = 800;
      } else {
        const words = label.split(/[\s\-_/(),.:]+/);
        if (words.some((w) => w.startsWith(raw))) {
          score = 650;
        } else if (label.includes(raw)) {
          score = 500;
        } else if (kind === raw || kind.startsWith(raw)) {
          score = 450;
        } else {
          const metaWords = meta.split(/[\s\-_/(),.:]+/);
          if (metaWords.some((w) => w.startsWith(raw))) {
            score = 400;
          } else if (meta.includes(raw)) {
            score = 300;
          } else if (keywords.includes(raw)) {
            score = 200;
          } else {
            const tokens = raw.split(/\s+/).filter(Boolean);
            if (tokens.length > 1) {
              const haystack = `${label} ${meta} ${kind} ${keywords}`;
              if (tokens.every((t) => haystack.includes(t))) {
                score = 350;
              } else if (tokens.some((t) => haystack.includes(t))) {
                score = 120;
              }
            }
          }
        }
      }

      if (score > 0) {
        scored.push({ entry, score });
      }
    }

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((s) => s.entry);
  }, [query, index]);

  useEffect(() => { setHighlighted(0); }, [query]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setHighlighted(0);
  }, []);

  const goToResult = useCallback((entry) => {
    setQuery('');
    closeSearch();
    setDrawerOpen(false);
    navigate(entry.to);
  }, [closeSearch, navigate]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (results[highlighted]) {
      goToResult(results[highlighted]);
      return;
    }
    if (results.length > 0) {
      goToResult(results[0]);
      return;
    }
    if (!query.trim()) return;
    const q = query.trim().toLowerCase();
    closeSearch();
    setDrawerOpen(false);
    if (q.includes('doctor') || q.includes('channel') || q.includes('appointment')) {
      navigate('/dashboard?tab=channeling');
    } else if (q.includes('drug') || q.includes('pharma') || q.includes('medicine')) {
      navigate('/pharma');
    } else if (q.includes('calc')) {
      navigate('/calculators');
    } else if (q.includes('book') || q.includes('library')) {
      navigate('/library');
    } else if (q.includes('article') || q.includes('news')) {
      navigate('/articles');
    } else if (q.includes('organ') || q.includes('anatomy')) {
      navigate('/anatomy');
    } else {
      navigate(`/diseases?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') { closeSearch(); e.currentTarget.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => (i + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => (i - 1 + results.length) % results.length); }
  };

  // Ctrl/Cmd+K is where people reach for search now.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        loadIndex();
        setSearchOpen(true);
        searchRef.current?.querySelector('input')?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loadIndex]);

  // --- dismissals ----------------------------------------------------------
  useEffect(() => {
    const onPointerDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) closeSearch();
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
        setProfileSection(null);
      }
      if (learningsRef.current && !learningsRef.current.contains(e.target)) setLearningsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [closeSearch]);

  useEffect(() => {
    setDrawerOpen(false);
    setAccountOpen(false);
    setProfileSection(null);
    setLearningsOpen(false);
    setDrawerLearningsOpen(false);
    closeSearch();
  }, [location.pathname, closeSearch]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [drawerOpen]);

  const initial = user?.full_name?.[0]?.toUpperCase() || 'U';

  // Toggle a profile accordion section
  const toggleProfileSection = (section) => {
    setProfileSection((prev) => (prev === section ? null : section));
  };

  const emptyResults = (variant) => (
    <div className={`dp-nav-results dp-nav-results--${variant}`} role="region" aria-label="Search suggestions">
      <div className="p-3 text-center">
        <div className="text-xs font-semibold text-[#1E1E24] dark:text-white">
          No direct matches for "{query.trim()}"
        </div>
        <p className="text-[11px] text-[#707078] mt-1">
          Explore key medical sections on Dr. Pulse:
        </p>
        <div className="grid grid-cols-2 gap-1.5 mt-2.5">
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/diseases'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <Stethoscope className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Diseases</span>
          </button>
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/pharma'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <Pill className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Medicines</span>
          </button>
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/anatomy'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <Activity className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Anatomy</span>
          </button>
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/dashboard?tab=channeling'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <UserCheck className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Doctors</span>
          </button>
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/library'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Library & Books</span>
          </button>
          <button
            type="button"
            onClick={() => { closeSearch(); navigate('/calculators'); }}
            className="text-left px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 text-[#B40026] flex items-center gap-1.5 transition"
          >
            <Calculator className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Calculators</span>
          </button>
        </div>
      </div>
    </div>
  );

  const resultList = (variant) => (
    <div className={`dp-nav-results dp-nav-results--${variant}`} role="listbox">
      <div className="dp-nav-results-list">
        {results.map((entry, i) => (
          <button
            key={entry.id}
            type="button"
            role="option"
            aria-selected={i === highlighted}
            className={`dp-nav-result ${i === highlighted ? 'is-active' : ''}`}
            onMouseEnter={() => setHighlighted(i)}
            onClick={() => goToResult(entry)}
          >
            <span className="dp-nav-result-icon">
              {entry.Icon && <entry.Icon className="w-4 h-4" />}
            </span>
            <span className="dp-nav-result-text">
              <span className="dp-nav-result-label">{entry.label}</span>
              <span className="dp-nav-result-meta">{entry.meta}</span>
            </span>
            <span className="dp-nav-result-kind">{entry.kind}</span>
            <ChevronRight className="w-4 h-4 dp-nav-result-chevron" />
          </button>
        ))}
      </div>
      <div className="dp-nav-results-foot">
        <span className="text-[11px] text-[#707078]">
          Found {results.length} result{results.length > 1 ? 's' : ''} across website
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-[#707078]">
          <CornerDownLeft className="w-3 h-3 text-[#B40026]" /> <span>Press Enter to open</span>
        </span>
      </div>
    </div>
  );

  /* -------------------------------------------------------------- */
  /*  Profile menu (5 accordion sections)                            */
  /* -------------------------------------------------------------- */
  const profileMenu = () => (
    <div className="dp-nav-menu dp-nav-menu--profile">
      {/* Header: photo + name + upgrade */}
      <div className="dp-nav-menu-head">
        <span className="dp-nav-avatar dp-nav-avatar--lg">
          {user?.image
            ? <img src={user.image} alt="" className="dp-nav-avatar-img" />
            : initial}
        </span>
        <span>
          <strong>{user.full_name}</strong>
          <em>{user.email}</em>
          {(isAdmin || isDoctor || isStudent || user?.verification_status === 'pending') && (
            <span className="dp-profile-role">
              {isAdmin
                ? '👑 System Administrator'
                : isDoctor
                ? '🩺 Medical Doctor'
                : isStudent
                ? '🎓 Medical Student'
                : '⏳ Verification in Progress'}
            </span>
          )}
        </span>
      </div>

      {/* Professional Upgrade Option for standard members */}
      {!isAdmin && !isDoctor && !isStudent && !isUpgraded && (
        <div className="p-3 border-b border-black/5 dark:border-white/10 bg-gradient-to-r from-rose-500/10 via-amber-500/5 to-indigo-500/10">
          <Link
            to="/dashboard?tab=profile&action=upgrade"
            onClick={() => {
              setAccountOpen(false);
              setProfileSection(null);
              window.dispatchEvent(new CustomEvent('open-upgrade-section'));
            }}
            className="dp-profile-cta-btn flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-white text-xs font-bold shadow-md hover:brightness-110 transition group"
            style={{
              background: 'linear-gradient(135deg, #F43F5E, #E11D48)',
              color: '#FFFFFF',
            }}
          >
            <span className="flex items-center gap-2 font-bold tracking-wide" style={{ color: '#FFFFFF' }}>
              <ShieldCheck className="w-4 h-4 text-white" style={{ color: '#FFFFFF' }} />
              <span style={{ color: '#FFFFFF' }}>{user?.verification_status === 'pending' ? 'Verification in Progress (24h)' : '⚡ Upgrade Account (Student / Doctor)'}</span>
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-white/90 group-hover:translate-x-0.5 transition-transform" style={{ color: '#FFFFFF' }} />
          </Link>
        </div>
      )}

      {/* Student Campus LMS portal quick access */}
      {isStudent && (
        <div className="p-3 border-b border-black/5 dark:border-white/10 bg-indigo-50/50 dark:bg-indigo-950/20">
          <Link
            to="/lms"
            onClick={() => { setAccountOpen(false); setProfileSection(null); }}
            className="dp-profile-cta-btn flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-white text-xs font-bold shadow hover:brightness-110 transition group"
            style={{
              background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
              color: '#FFFFFF',
            }}
          >
            <span className="flex items-center gap-2 font-bold tracking-wide" style={{ color: '#FFFFFF' }}>
              <GraduationCap className="w-4 h-4 text-white" style={{ color: '#FFFFFF' }} />
              <span style={{ color: '#FFFFFF' }}>Campus LMS Portal</span>
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-white/80 group-hover:translate-x-0.5 transition-transform" style={{ color: '#FFFFFF' }} />
          </Link>
        </div>
      )}

      {/* Admin Panel quick access for administrator */}
      {isAdmin && (
        <div className="p-3 border-b border-black/5 dark:border-white/10 bg-rose-50/50 dark:bg-rose-950/20">
          <Link
            to="/admin"
            onClick={() => { setAccountOpen(false); setProfileSection(null); }}
            className="dp-profile-cta-btn flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-white text-xs font-bold shadow hover:brightness-110 transition group"
            style={{
              background: 'linear-gradient(135deg, #F43F5E, #E11D48)',
              color: '#FFFFFF',
            }}
          >
            <span className="flex items-center gap-2 font-bold tracking-wide" style={{ color: '#FFFFFF' }}>
              <ShieldCheck className="w-4 h-4 text-white" style={{ color: '#FFFFFF' }} />
              <span style={{ color: '#FFFFFF' }}>Admin Panel</span>
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-white/80 group-hover:translate-x-0.5 transition-transform" style={{ color: '#FFFFFF' }} />
          </Link>
        </div>
      )}

      {/* Patient Health Profile & Family Records quick access */}
      <div className="p-3 border-b border-black/5 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03]">
        <Link
          to="/dashboard?tab=profile"
          onClick={() => { setAccountOpen(false); setProfileSection(null); }}
          className="flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl bg-white dark:bg-[#202026] hover:bg-black/[0.03] dark:hover:bg-white/[0.05] border border-black/10 dark:border-white/10 shadow-sm transition group"
        >
          <span className="flex items-center gap-2 text-xs font-bold text-[#1E1E24] dark:text-white tracking-wide">
            <span className="w-6 h-6 rounded-lg bg-rose-500/10 text-[#B40026] dark:text-rose-400 flex items-center justify-center shrink-0">
              <User className="w-3.5 h-3.5" />
            </span>
            <span>Patient & Family Profiles</span>
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-[#707078] group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Settings */}
      <div className="dp-profile-section">
        <button
          type="button"
          className={`dp-profile-section-head ${profileSection === 'settings' ? 'is-open' : ''}`}
          onClick={() => toggleProfileSection('settings')}
        >
          <span className="dp-profile-section-num">①</span>
          <Settings className="w-4 h-4" />
          <span>Settings</span>
          <ChevronDown className={`w-4 h-4 dp-profile-chevron ${profileSection === 'settings' ? 'is-rotated' : ''}`} />
        </button>
        {profileSection === 'settings' && (
          <div className="dp-profile-section-body">
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <Globe className="w-4 h-4" />
              <span>Language</span>
              <span className="dp-profile-item-value">English</span>
            </button>
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <Shield className="w-4 h-4" />
              <span>Privacy Centre</span>
              <ChevronRight className="w-3.5 h-3.5 dp-profile-item-arrow" />
            </button>
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <Settings className="w-4 h-4" />
              <span>Settings</span>
              <ChevronRight className="w-3.5 h-3.5 dp-profile-item-arrow" />
            </button>
          </div>
        )}
      </div>

      {/* ② Account */}
      <div className="dp-profile-section">
        <button
          type="button"
          className={`dp-profile-section-head ${profileSection === 'account' ? 'is-open' : ''}`}
          onClick={() => toggleProfileSection('account')}
        >
          <span className="dp-profile-section-num">②</span>
          <User className="w-4 h-4" />
          <span>Account</span>
          <ChevronDown className={`w-4 h-4 dp-profile-chevron ${profileSection === 'account' ? 'is-rotated' : ''}`} />
        </button>
        {profileSection === 'account' && (
          <div className="dp-profile-section-body">
            <div className="dp-profile-item dp-profile-item--info">
              <User className="w-4 h-4" />
              <span>Username</span>
              <span className="dp-profile-item-value">{user?.full_name || '—'}</span>
            </div>
            <div className="dp-profile-item dp-profile-item--info">
              <Phone className="w-4 h-4" />
              <span>Mobile No</span>
              <span className="dp-profile-item-value">{user?.phone || '—'}</span>
            </div>
            <div className="dp-profile-item dp-profile-item--info">
              <Mail className="w-4 h-4" />
              <span>Email</span>
              <span className="dp-profile-item-value">{user?.email || '—'}</span>
            </div>
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <Key className="w-4 h-4" />
              <span>Change Password</span>
              <ChevronRight className="w-3.5 h-3.5 dp-profile-item-arrow" />
            </button>
          </div>
        )}
      </div>

      {/* ③ Help & Support */}
      <div className="dp-profile-section">
        <button
          type="button"
          className={`dp-profile-section-head ${profileSection === 'help' ? 'is-open' : ''}`}
          onClick={() => toggleProfileSection('help')}
        >
          <span className="dp-profile-section-num">③</span>
          <HelpCircle className="w-4 h-4" />
          <span>Help & Support</span>
          <ChevronDown className={`w-4 h-4 dp-profile-chevron ${profileSection === 'help' ? 'is-rotated' : ''}`} />
        </button>
        {profileSection === 'help' && (
          <div className="dp-profile-section-body">
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <HelpCircle className="w-4 h-4" />
              <span>Help Centre</span>
              <span className="dp-profile-item-sub">Q & A</span>
            </button>
            <a
              href="https://wa.me/+94XXXXXXXXX"
              target="_blank"
              rel="noopener noreferrer"
              className="dp-profile-item"
              onClick={() => { setAccountOpen(false); setProfileSection(null); }}
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp Support</span>
              <ChevronRight className="w-3.5 h-3.5 dp-profile-item-arrow" />
            </a>
          </div>
        )}
      </div>

      {/* ④ Display */}
      <div className="dp-profile-section">
        <button
          type="button"
          className={`dp-profile-section-head ${profileSection === 'display' ? 'is-open' : ''}`}
          onClick={() => toggleProfileSection('display')}
        >
          <span className="dp-profile-section-num">④</span>
          <Monitor className="w-4 h-4" />
          <span>Display</span>
          <ChevronDown className={`w-4 h-4 dp-profile-chevron ${profileSection === 'display' ? 'is-rotated' : ''}`} />
        </button>
        {profileSection === 'display' && (
          <div className="dp-profile-section-body">
            <button type="button" className="dp-profile-item" onClick={() => { setAccountOpen(false); setProfileSection(null); }}>
              <Mail className="w-4 h-4" />
              <span>Report Problem</span>
              <ChevronRight className="w-3.5 h-3.5 dp-profile-item-arrow" />
            </button>
            <button
              type="button"
              className="dp-profile-item"
              onClick={(e) => { toggleTheme(e); }}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              <span className="dp-profile-item-value dp-profile-theme-badge">
                {theme === 'dark' ? '🌞' : '🌙'}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* ⑤ Logout */}
      <div className="dp-profile-section dp-profile-section--logout">
        <button
          type="button"
          className="dp-profile-section-head dp-nav-menu-out"
          onClick={() => { logout(); setAccountOpen(false); setProfileSection(null); navigate('/'); }}
        >
          <span className="dp-profile-section-num">⑤</span>
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* The header carries a transform for the hide-on-scroll, which would
          make it the containing block for any position:fixed child. The drawer
          therefore has to be its sibling, not its descendant. */}
      <header
        className={`dp-navbar dp-nav ${hidden ? 'is-hidden' : ''}`}
      >
      <div className="dp-nav-shell">
        <div className="dp-nav-bar">
          <Link
            to={isDoctor ? '/doctor/dashboard' : isStudent ? '/lms' : '/'}
            className="dp-nav-brand"
          >
            <img src="/images/newLogo.PNG" alt="Dr. Pulse" />
            <span className="dp-nav-brand-text">
              <span className="dp-nav-brand-name">Dr. <span>Pulse</span></span>
              <span className="dp-script dp-nav-brand-tag">
                Rhythm of Care
              </span>
            </span>
          </Link>

          <nav className="dp-nav-links" ref={navRef} aria-label="Main">
            <span
              className={`dp-nav-pill ${pill.visible ? 'is-visible' : ''}`}
              style={{ transform: `translateX(${pill.left}px)`, width: pill.width }}
              aria-hidden="true"
            />
            {navList.map((n) => {
              /* ---------- dropdown (Learnings) ---------- */
              if (n.isDropdown) {
                const anyChildActive = n.children?.some((c) => isActiveNav(location, c.to));
                return (
                  <div key={n.label} className="dp-nav-dropdown-wrap" ref={learningsRef}>
                    <button
                      type="button"
                      className={`dp-nav-link dp-nav-dropdown-trigger ${anyChildActive ? 'is-active' : ''}`}
                      onClick={() => setLearningsOpen((v) => !v)}
                      aria-expanded={learningsOpen}
                    >
                      <span className="dp-nav-link-row">
                        {n.Icon && <n.Icon className="w-3.5 h-3.5 opacity-80" />}
                        <span>{n.label}</span>
                        <ChevronDown className={`w-3.5 h-3.5 dp-dropdown-chevron ${learningsOpen ? 'is-rotated' : ''}`} />
                      </span>
                      <span className="dp-nav-underline" aria-hidden="true" />
                    </button>
                    {learningsOpen && (
                      <div className="dp-nav-dropdown-panel">
                        {n.children.map((child) => {
                          const active = isActiveNav(location, child.to);
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              className={`dp-nav-dropdown-item ${active ? 'is-active' : ''}`}
                              onClick={() => setLearningsOpen(false)}
                            >
                              {child.Icon && <child.Icon className="w-4 h-4" />}
                              <span>{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const active = n.to && isActiveNav(location, n.to);
              if (n.disabled) {
                return (
                  <span key={n.to} aria-disabled="true" title={n.note} className="dp-nav-link is-disabled">
                    <span className="dp-nav-link-row">
                      <span>{n.label}</span>
                    </span>
                    <span className="dp-nav-soon">{n.note}</span>
                  </span>
                );
              }
              if (n.highlight) {
                return (
                  <Link key={n.to} to={n.to} className={`dp-nav-cta ${active ? 'is-active' : ''} inline-flex items-center gap-1.5`}>
                    {n.Icon && <n.Icon className="w-3.5 h-3.5" />}
                    <span>{n.label}</span>
                  </Link>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  ref={(node) => { itemRefs.current[n.to] = node; }}
                  aria-current={active ? 'page' : undefined}
                  className={`dp-nav-link ${active ? 'is-active' : ''}`}
                >
                  <span className="dp-nav-link-row">
                    {n.Icon && <n.Icon className="w-3.5 h-3.5 opacity-80" />}
                    <span>{n.label}</span>
                  </span>
                  <span className="dp-nav-underline" aria-hidden="true" />
                </Link>
              );
            })}
          </nav>

          <div className="dp-nav-search" ref={searchRef}>
            <form onSubmit={onSubmit} className={`dp-nav-search-field ${searchOpen ? 'is-open' : ''}`} role="search">
              <Search className="w-4 h-4 dp-nav-search-icon" />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => { loadIndex(); setSearchOpen(true); }}
                onKeyDown={onSearchKeyDown}
                placeholder="Search here..."
                aria-label="Search the site"
                autoComplete="off"
              />
            </form>
            {searchOpen && query.trim() !== '' && (
              results.length > 0
                ? resultList('desktop')
                : emptyResults('desktop')
            )}
          </div>

          <div className="dp-nav-actions">
            <button
              type="button"
              onClick={toggleTheme}
              className="dp-nav-icon-btn"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {!user ? (
              <Link to="/login" className="dp-nav-login">Login</Link>
            ) : (
              <div className="flex items-center gap-2">
                <div className="dp-nav-account" ref={accountRef}>
                  <button type="button" onClick={() => { setAccountOpen((v) => !v); if (!accountOpen) setProfileSection(null); }} className="dp-nav-avatar-btn" aria-expanded={accountOpen}>
                    <span className="dp-nav-avatar">
                      {user?.image
                        ? <img src={user.image} alt="" className="dp-nav-avatar-img" />
                        : initial}
                    </span>
                    <span className="dp-nav-avatar-name">{user.full_name?.split(' ')[0]}</span>
                  </button>
                  {accountOpen && profileMenu()}
                </div>
              </div>
            )}

            <button
              type="button"
              className="dp-nav-icon-btn dp-nav-burger"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {showProgress && (
            <span className="dp-nav-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />
          )}
        </div>
      </div>
      </header>

      {/* --- mobile drawer --- */}
      <div className={`dp-drawer ${drawerOpen ? 'is-open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="dp-drawer-scrim" onClick={() => setDrawerOpen(false)} />
        <div className="dp-drawer-panel" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="dp-drawer-head">
            <Link
              to={isDoctor ? '/doctor/dashboard' : isStudent ? '/lms' : '/'}
              className="dp-nav-brand"
              onClick={() => setDrawerOpen(false)}
            >
              <img src="/images/newLogo.PNG" alt="Dr. Pulse" />
              <span className="dp-nav-brand-text">
                <span className="dp-nav-brand-name">Dr. <span>Pulse</span></span>
                <span className="dp-script dp-nav-brand-tag">
                  Rhythm of Care
                </span>
              </span>
            </Link>
            <button type="button" onClick={() => setDrawerOpen(false)} className="dp-nav-icon-btn" aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="dp-drawer-search" role="search">
            <Search className="w-4 h-4" />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
              onFocus={loadIndex}
              onKeyDown={onSearchKeyDown}
              placeholder="Search here..."
              aria-label="Search the site"
              autoComplete="off"
            />
          </form>
          {query.trim() !== '' && (
            results.length > 0
              ? resultList('drawer')
              : emptyResults('drawer')
          )}

          <nav className="dp-drawer-links" aria-label="Main">
            {navList.map((n, i) => {
              const style = { animationDelay: `${60 + i * 35}ms` };

              /* ---------- dropdown (Learnings) in drawer ---------- */
              if (n.isDropdown) {
                return (
                  <div key={n.label} style={style} className="dp-drawer-dropdown">
                    <button
                      type="button"
                      className="dp-drawer-link dp-drawer-dropdown-trigger"
                      onClick={() => setDrawerLearningsOpen((v) => !v)}
                      style={style}
                    >
                      <span className="flex items-center gap-2">
                        {n.Icon && <n.Icon className="w-4 h-4 text-[#B40026]" />}
                        <span>{n.label}</span>
                      </span>
                      <ChevronDown className={`w-4 h-4 dp-dropdown-chevron ${drawerLearningsOpen ? 'is-rotated' : ''}`} />
                    </button>
                    {drawerLearningsOpen && (
                      <div className="dp-drawer-dropdown-items">
                        {n.children.map((child) => {
                          const active = isActiveNav(location, child.to);
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setDrawerOpen(false)}
                              className={`dp-drawer-dropdown-item ${active ? 'is-active' : ''}`}
                            >
                              {child.Icon && <child.Icon className="w-4 h-4" />}
                              <span>{child.label}</span>
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const active = n.to && isActiveNav(location, n.to);
              if (n.disabled) {
                return (
                  <span key={n.to} style={style} aria-disabled="true" className="dp-drawer-link is-disabled">
                    {n.label}<span className="dp-nav-soon">{n.note}</span>
                  </span>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  style={style}
                  onClick={() => setDrawerOpen(false)}
                  className={`dp-drawer-link ${active ? 'is-active' : ''} ${n.highlight ? 'is-cta' : ''}`}
                >
                  <span className="flex items-center gap-2">
                    {n.Icon && <n.Icon className="w-4 h-4 text-[#B40026]" />}
                    <span>{n.label}</span>
                  </span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              );
            })}
          </nav>

          <div className="dp-drawer-foot">
            {!user ? (
              <div className="flex flex-col gap-2 w-full">
                <Link to="/library" onClick={() => setDrawerOpen(false)} className="dp-drawer-ghost text-center font-bold text-[#B40026]">
                  📚 Browse Library & Buy Books
                </Link>
                <Link to="/login" onClick={() => setDrawerOpen(false)} className="dp-drawer-primary">Login</Link>
              </div>
            ) : (
              <>
                <div className="dp-drawer-user">
                  <span className="dp-nav-avatar dp-nav-avatar--lg">
                    {user?.image
                      ? <img src={user.image} alt="" className="dp-nav-avatar-img" />
                      : initial}
                  </span>
                  <span>
                    <strong>{user.full_name}</strong>
                    <em>{user.email}</em>
                    {(isAdmin || isDoctor || isStudent || user?.verification_status === 'pending') && (
                      <span className="block text-xs text-[#B40026] font-semibold mt-0.5">
                        {isAdmin
                          ? '👑 System Administrator'
                          : isDoctor
                          ? `🩺 ${user.specialization || 'Medical Doctor'}`
                          : isStudent
                          ? '🎓 Medical Student'
                          : '⏳ Verification Pending'}
                      </span>
                    )}
                  </span>
                </div>
                <div className="dp-drawer-foot-actions">
                  {!isAdmin && !isDoctor && !isStudent && !isUpgraded && (
                    <Link
                      to="/dashboard?tab=profile&action=upgrade"
                      onClick={() => {
                        setDrawerOpen(false);
                        window.dispatchEvent(new CustomEvent('open-upgrade-section'));
                      }}
                      className="dp-drawer-primary font-bold flex items-center justify-center gap-2 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #F43F5E, #E11D48)', color: '#FFFFFF' }}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span>{user?.verification_status === 'pending' ? 'Verification in Progress' : '⚡ Upgrade Account'}</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setDrawerOpen(false)}
                      className="dp-drawer-primary font-bold flex items-center justify-center gap-2"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span>Admin Control Panel</span>
                    </Link>
                  )}
                  <Link
                    to={isAdmin ? '/admin' : isDoctor ? '/doctor/dashboard' : isStudent ? '/lms' : '/library'}
                    onClick={() => setDrawerOpen(false)}
                    className="dp-drawer-ghost font-bold text-[#B40026]"
                  >
                    {isAdmin ? '👑 Admin Operations' : isDoctor ? '🩺 Doctor Workspace' : isStudent ? '🎓 Campus LMS' : '📚 Medical Library & Books'}
                  </Link>
                  <Link to="/dashboard?tab=profile" onClick={() => setDrawerOpen(false)} className="dp-drawer-ghost font-bold text-[#B40026]">
                    Patient & Family Profiles
                  </Link>
                  <Link to="/dashboard" onClick={() => setDrawerOpen(false)} className="dp-drawer-ghost">
                    Dashboard
                  </Link>
                  <button type="button" onClick={() => { logout(); setDrawerOpen(false); }} className="dp-drawer-primary">Logout</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
